import {
	IconBrandGithub,
	IconBrandLinkedin,
	IconDownload,
	IconMail,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { config } from "@/data/config";
import { educationData } from "@/data/resume/education";
import { resumeData } from "@/data/resume/experience";
import { skillsData } from "@/data/resume/skills";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/resume")({
	component: RouteComponent,
	head: () => {
		const base = seo({
			title: `Resume | ${config.Name}`,
			description: `Professional resume of ${config.Name} — software engineer focused on email infrastructure, deliverability, and developer tooling.`,
			keywords: `resume, cv, software engineer, email infrastructure, email deliverability, ${config.Name.toLowerCase()}`,
			url: `${config.Url}/resume`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(schema.profilePage(`${config.Url}/resume`)),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Resume", url: `${config.Url}/resume` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-40"
				aria-hidden
			/>
			<main className="relative pt-12 sm:pt-20" id="resume">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Resume"
							description="Experience, education, and skills. Download a PDF or jump to a section."
						/>
					</div>
					<div className="flex flex-col gap-10 pb-20 lg:flex-row lg:gap-14">
						<div className="w-full flex-1 lg:top-8 lg:mt-0 lg:self-start lg:py-4">
							<div className="flex flex-col items-center lg:items-start">
								<Accordion
									type="single"
									collapsible
									className="w-full animate-in fade-in slide-in-from-bottom-8 duration-800 delay-200 fill-mode-both"
									defaultValue="intro"
								>
									<AccordionItem value="intro" id="intro">
										<AccordionTrigger className="text-2xl font-semibold cursor-pointer hover:no-underline">
											Intro
										</AccordionTrigger>
										<hr className="mb-4 w-full border-t border-dashed border-border/80" />
										<AccordionContent>
											<p className="text-base text-pretty text-foreground">
												I am a software engineer with a passion for Email, and
												all things surrounding it. I have been working in the
												industry for almost 10 years. I originally started out
												on the Email deliverability and anti-abuse side of the
												industry, and have since transitioned into a full-stack
												role. I have a passion for building tools that help
												people and businesses, with a focus on user experience.
											</p>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
								<Accordion
									type="single"
									collapsible
									className="w-full animate-in fade-in slide-in-from-bottom-8 duration-800 delay-400 fill-mode-both"
									defaultValue="experience"
								>
									<AccordionItem value="experience" id="experience">
										<AccordionTrigger className="text-2xl font-semibold cursor-pointer hover:no-underline">
											Experience
										</AccordionTrigger>
										<hr className="mb-4 w-full border-t border-dashed border-border/80" />
										<AccordionContent>
											{/* Mobile: traditional stacked layout */}
											<div className="lg:hidden">
												<div className="space-y-8">
													{resumeData.map((entry) => (
														<div
															key={`${entry.title}-${entry.date.start}-${entry.company.name}`}
															className="flex-1"
														>
															<Card className="bg-transparent border-none shadow-none p-0 gap-0">
																<CardHeader>
																	<CardTitle className="text-lg font-semibold tracking-tight mb-1">
																		{entry.title}
																	</CardTitle>
																	<div className="flex flex-wrap items-center gap-x-2 text-muted-foreground text-sm font-normal">
																		<a
																			href={entry.company.link}
																			target="_blank"
																			rel="noopener noreferrer"
																			className="hover:underline transition-all duration-300 ease-in-out"
																		>
																			{entry.company.name}
																		</a>
																		{entry.company.location && (
																			<span className="italic">
																				{`- ${entry.company.location}`}
																			</span>
																		)}
																		<span className="text-xs">
																			{entry.date.start}
																			{entry.date.end
																				? ` - ${entry.date.end}`
																				: " - Present"}
																		</span>
																	</div>
																</CardHeader>
																<CardContent className="pt-0 text-muted-foreground">
																	{entry.description && (
																		<p className="text-sm my-2">
																			{entry.description}
																		</p>
																	)}
																	<ul className="list-disc list-inside space-y-1">
																		{entry.items?.map((item) => (
																			<li key={item} className="text-sm">
																				{item}
																			</li>
																		))}
																	</ul>
																</CardContent>
															</Card>
														</div>
													))}
												</div>
											</div>

											{/* Desktop: existing timeline */}
											<div className="hidden lg:block">
												<div className="relative mx-auto max-w-4xl">
													{resumeData.map((entry, index) => (
														<div
															key={`${entry.title}-${entry.date.start}-${entry.company.name}`}
															className="relative mb-10 flex"
														>
															<div className="w-32 shrink-0 pr-4 text-right">
																<div className="text-sm text-muted-foreground">
																	{entry.date.start}
																	{entry.date.end ? ` - ${entry.date.end}` : ""}
																</div>
															</div>

															<div className="relative flex flex-col items-center">
																<div className="bg-foreground flex size-4 items-center justify-center rounded-full" />
																{index < resumeData.length - 1 && (
																	<div className="bg-muted w-px flex-1 mt-2" />
																)}
															</div>

															<div className="flex-1 pl-2 ">
																<Card className="bg-transparent border-none shadow-none p-0 gap-0">
																	<CardHeader>
																		<CardTitle className="text-xl font-semibold tracking-tight mb-2 flex flex-col">
																			{entry.title}
																			<div className="flex flex-row gap-1 text-muted-foreground text-sm font-normal">
																				<a
																					href={entry.company.link}
																					target="_blank"
																					rel="noopener noreferrer"
																					className="hover:underline transition-all duration-300 ease-in-out"
																				>
																					{entry.company.name}
																				</a>
																				{entry.company.location && (
																					<span className="italic">
																						{`- ${entry.company.location}`}
																					</span>
																				)}
																			</div>
																		</CardTitle>
																	</CardHeader>
																	<CardContent className="pt-0 text-muted-foreground">
																		{entry.description && (
																			<p className="text-sm mb-2">
																				{entry.description}
																			</p>
																		)}
																		<ul className="list-disc list-inside">
																			{entry.items?.map((item) => (
																				<li key={item}>{item}</li>
																			))}
																		</ul>
																	</CardContent>
																</Card>
															</div>
														</div>
													))}
												</div>
											</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
								<Accordion
									type="single"
									collapsible
									className="w-full animate-in fade-in slide-in-from-bottom-8 duration-800 delay-600 fill-mode-both"
									defaultValue="education"
								>
									<AccordionItem value="education" id="education">
										<AccordionTrigger className="text-2xl font-semibold cursor-pointer hover:no-underline">
											Education
										</AccordionTrigger>
										<hr className="mb-4 w-full border-t border-dashed border-border/80" />
										<AccordionContent>
											{/* Mobile: traditional stacked layout */}
											<div className="lg:hidden">
												<div className="space-y-8">
													{educationData.map((entry) => (
														<div
															key={`${entry.degree}-${entry.date.start}-${entry.school}`}
															className="flex-1"
														>
															<Card className="bg-transparent border-none shadow-none p-0 gap-0">
																<CardHeader>
																	<CardTitle className="text-lg font-semibold tracking-tight mb-1">
																		{entry.degree}
																	</CardTitle>
																	<div className="text-muted-foreground text-sm font-normal">
																		{entry.school} • {entry.date.start}
																		{entry.date.end
																			? ` - ${entry.date.end}`
																			: " - Present"}
																	</div>
																</CardHeader>
																<CardContent className="pt-0 text-muted-foreground">
																	{entry.description && (
																		<p className="text-sm my-2">
																			{entry.description}
																		</p>
																	)}
																</CardContent>
															</Card>
														</div>
													))}
												</div>
											</div>

											{/* Desktop: existing timeline */}
											<div className="hidden lg:block">
												<div className="relative mx-auto max-w-4xl">
													{educationData.map((entry, index) => (
														<div
															key={`${entry.degree}-${entry.date.start}-${entry.school}`}
															className="relative mb-10 flex"
														>
															<div className="w-32 shrink-0 pr-4 text-right">
																<div className="text-sm text-muted-foreground">
																	{entry.date.start}
																	{entry.date.end
																		? ` - ${entry.date.end}`
																		: " - Present"}
																</div>
															</div>

															<div className="relative flex flex-col items-center">
																<div className="bg-foreground flex size-4 items-center justify-center rounded-full" />
																{index < educationData.length - 1 && (
																	<div className="bg-muted w-px flex-1 mt-2" />
																)}
															</div>

															<div className="flex-1 pl-2 ">
																<Card className="bg-transparent border-none shadow-none p-0 gap-0">
																	<CardHeader>
																		<CardTitle className="text-xl font-semibold tracking-tight mb-2 flex flex-col">
																			{entry.degree}
																			<div className="flex flex-row gap-1 text-muted-foreground text-sm font-normal">
																				{entry.school}
																			</div>
																		</CardTitle>
																	</CardHeader>
																	<CardContent className="pt-0 text-muted-foreground">
																		{entry.description && (
																			<p className="text-sm mb-2">
																				{entry.description}
																			</p>
																		)}
																	</CardContent>
																</Card>
															</div>
														</div>
													))}
												</div>
											</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
								<Accordion
									type="single"
									collapsible
									className="w-full animate-in fade-in slide-in-from-bottom-8 duration-800 delay-800 fill-mode-both"
									defaultValue="skills"
								>
									<AccordionItem value="skills" id="skills">
										<AccordionTrigger className="text-2xl font-semibold cursor-pointer hover:no-underline">
											Skills
										</AccordionTrigger>
										<hr className="mb-4 w-full border-t border-dashed border-border/80" />
										<AccordionContent>
											{skillsData.map((entry) => (
												<div key={entry.name} className="mb-4">
													<h3 className="text-lg font-semibold mb-2">
														{entry.name}
													</h3>
													<div className="flex flex-wrap gap-2">
														{entry.items.map((item) => (
															<Badge variant="outline" key={item}>
																{item}
															</Badge>
														))}
													</div>
												</div>
											))}
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</div>
						</div>
						<aside className="mt-8 hidden flex-col justify-center animate-in fade-in slide-in-from-bottom-8 duration-800 delay-1000 fill-mode-both lg:mt-0 lg:flex lg:w-72 lg:shrink-0 lg:justify-start lg:py-4">
							<div className="flex flex-col items-center lg:items-start">
								<div className="relative mb-6">
									<div
										className="absolute inset-6 rounded-3xl bg-linear-to-br from-primary/20 via-transparent to-accent/15 blur-2xl dark:hidden"
										aria-hidden
									/>
									<div
										className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-foreground/[0.06] dark:ring-white/10"
										aria-hidden
									/>
									<img
										src="/images/avatar.png"
										alt={`${config.Name} headshot`}
										width={256}
										height={256}
										className="relative size-56 rounded-3xl object-cover shadow-xl dark:shadow-none lg:size-64"
									/>
								</div>
								<div className="mb-6 text-center lg:text-left">
									<Button
										asChild
										variant="outline"
										size="sm"
										className="mb-4 w-full rounded-lg border-border/80"
									>
										<a
											href="/resume.pdf"
											download={`${config.Name.replace(/\s+/g, "-")}-Resume.pdf`}
											target="_blank"
											rel="noopener noreferrer"
										>
											<IconDownload className="mr-2 size-4" />
											Download PDF
										</a>
									</Button>
									<h2 className="text-xl font-semibold mb-2">
										Contact Information
									</h2>
									<div className="space-y-1 text-sm text-muted-foreground ">
										{config.Email && (
											<p
												className="flex items-center gap-2 hover:text-foreground transition-all duration-300 ease-in-out"
												title="Email"
											>
												<IconMail className="size-4" />{" "}
												<a
													href={`mailto:${config.Email}`}
													target="_blank"
													rel="noopener noreferrer"
												>
													{config.Email}
												</a>
											</p>
										)}
										{config.Socials.Linkedin && (
											<p
												className="flex items-center gap-2 hover:text-foreground transition-all duration-300 ease-in-out"
												title="LinkedIn Profile"
											>
												<IconBrandLinkedin className="size-4" />{" "}
												<a
													href={config.Socials.Linkedin}
													target="_blank"
													rel="noopener noreferrer"
												>
													{config.Socials.Linkedin}
												</a>
											</p>
										)}
										{config.Socials.Github && (
											<p
												className="flex items-center gap-2 hover:text-foreground transition-all duration-300 ease-in-out"
												title="GitHub Profile"
											>
												<IconBrandGithub className="size-4" />{" "}
												<a
													href={config.Socials.Github}
													target="_blank"
													rel="noopener noreferrer"
												>
													{config.Socials.Github}
												</a>
											</p>
										)}
									</div>
								</div>
							</div>

							<nav className="flex w-full flex-col gap-1 lg:sticky lg:top-24 lg:self-start lg:pt-4">
								<a
									href="/resume#intro"
									className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
								>
									Intro
								</a>
								<a
									href="/resume#experience"
									className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
								>
									Experience
								</a>
								<a
									href="/resume#education"
									className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
								>
									Education
								</a>
								<a
									href="/resume#skills"
									className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
								>
									Skills
								</a>
							</nav>
						</aside>
					</div>
				</div>
			</main>
		</div>
	);
}
